/**
 * Self-serve sign-in for a member who already has an account and is not
 * opening the mailed link on this device: email, then a six-digit code,
 * same credential a magic link is — see services/api/src/member-auth.ts.
 * Redemption goes through the exact same endpoint RedeemView uses, since a
 * code and a link token are both just a raw string handed to /member/redeem.
 */

import { useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import type { JSX } from 'preact';

import { apiPost, setSession, ApiError } from '../api.js';

export function LoginView(): JSX.Element {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function requestCode(event: Event): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiPost('/public/v1/member/login/request', { email: email.trim() });
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event: Event): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // The email goes along with the code because a staff preview code is
      // only good with the address it was mailed to — a member's own code
      // ignores it. See services/api/src/routes/member.ts.
      const result = await apiPost<{ session_token: string; redirect_hint: string }>('/public/v1/member/redeem', {
        token: code.trim(),
        email: email.trim(),
      });
      setSession(result.session_token);
      const next = new URLSearchParams(window.location.search).get('next');
      navigate(next || `/${result.redirect_hint}`, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "That code is wrong or has expired — request a new one."
          : 'Could not sign you in. Try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (step === 'email') {
    return (
      <main class="view auth-view">
        <header>
          <span class="ff-eyebrow">Your place at the farm</span>
          <h1>Welcome back.</h1>
          <p class="muted">Already a member? We'll email you a code.</p>
        </header>
        <form class="compose" onSubmit={(event) => void requestCode(event)}>
          <label>
            Email
            <input
              type="email"
              autoComplete="email"
              value={email}
              onInput={(event) => setEmail((event.target as HTMLInputElement).value)}
              required
            />
          </label>
          {error && <p class="error" role="alert">{error}</p>}
          <button class="primary" type="submit" disabled={busy || !email.trim()}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main class="view auth-view">
      <header>
        <span class="ff-eyebrow">One more step</span>
        <h1>Enter your code</h1>
        <p class="muted">
          If {email} has an account, a six-digit code just went out — it's good for 10 minutes.
        </p>
      </header>
      <form class="compose" onSubmit={(event) => void verifyCode(event)}>
        <label>
          Code
          <input
            value={code}
            onInput={(event) => setCode((event.target as HTMLInputElement).value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            required
            autoFocus
          />
        </label>
        {error && <p class="error" role="alert">{error}</p>}
        <button class="primary" type="submit" disabled={busy || code.trim().length === 0}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button class="link" type="button" onClick={() => setStep('email')}>
          Use a different email
        </button>
      </form>
    </main>
  );
}
